import { useEffect, useMemo, useState } from 'react'
import { isMultiCartFlightItem } from 'components/Pages/UserCartPage/SharedCarts/multiCartUtils'
import { isFlightItem } from 'lib/checkout/checkoutUtils'

const FLIGHT_CART_EXPIRY_TICK_MS = 1000

function hasActiveFlightExpiry(cartItemsMap: Map<string, App.MultiCartItem>): boolean {
  for (const cartItem of cartItemsMap.values()) {
    if (!isMultiCartFlightItem(cartItem)) continue
    if (!isFlightItem(cartItem.item)) continue
    if (cartItem.state === 'deleted') continue
    if (cartItem.item.expiry !== undefined && cartItem.item.expiry > Date.now()) return true
  }

  return false
}

/**
 * A flight still awaiting fare selection has no cart item, so `hasActiveFlightExpiry`
 * can't see it: its saved provider search lapses with nothing to re-render the row or
 * tile as it happens. Both surfaces read that expiry now (via `isSavedFlightSearchExpired`),
 * so the tick has to keep running while one is still live.
 */
function hasLiveSavedSearch(savedSearchExpiries: Array<number>): boolean {
  return savedSearchExpiries.some(expiry => expiry > Date.now())
}

export function useFlightCartExpiryTick(
  cartItemsMap: Map<string, App.MultiCartItem>,
  savedSearchExpiries: Array<number>,
): number {
  const [now, setNow] = useState(Date.now)
  const shouldTick = useMemo(
    () => hasActiveFlightExpiry(cartItemsMap) || hasLiveSavedSearch(savedSearchExpiries),
    [cartItemsMap, savedSearchExpiries],
  )

  useEffect(() => {
    setNow(Date.now())
    if (!shouldTick) return

    const interval = setInterval(() => {
      setNow(Date.now())
    }, FLIGHT_CART_EXPIRY_TICK_MS)

    return () => clearInterval(interval)
  }, [shouldTick])

  return now
}
